import React, { useState } from "react";
import {
  useUpdateAiAgent,
  useProviderSchema,
} from "../../../hooks/useAiAgents";
import {
  useToast,
  useFranchisesInfinite,
  useFranchise,
  usePostHog,
} from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { useTranslation } from "react-i18next";
import ProviderSettingsForm from "./ProviderSettingsForm";
import {
  maskAgentSetting,
  agentErrorForFeedback,
  agentConfigurationErrorKey,
} from "../../../lib/aiAgentSecrets";
import FranchiseSelect from "./FranchiseSelect";
import BranchSelectionModeField from "./BranchSelectionModeField";
import AgentWebviewColorsField from "./AgentWebviewColorsField";
import AgentSitesSection from "./AgentSitesSection";
import AgentTemplatesModal from "./AgentTemplatesModal";
import Modal from "../../ui/UIElements/Modal";
import FranchiseChangeConfirmationModal from "../../ui/Modals/FranchiseChangeConfirmationModal";
import { ContextHelp } from "../../ui";
import { Button } from "../../ui/UIElements/Button";
import { NativeSelect } from "../../ui/UIElements/Select";
import { convertTime } from "../../../lib/globalUtils";
import { useUiLocale } from "../../../hooks/useUiLocale";
import { resolveAgentSettingLabel } from "../../../lib/aiAgentI18n";
import {
  normalizeLanguageCode,
  normalizeSettingsLanguage,
  LANGUAGE_OPTIONS,
} from "../../../lib/aiAgentLanguage";
import {
  getAgentBranchSelectionMode,
  buildBranchSelectionSettings,
} from "../../../lib/aiAgentBranchSelection";
import {
  getAgentWebviewColors,
  buildWebviewColorsPatch,
} from "../../../lib/aiAgentWebviewColors";
import { useIntercomContext } from "@finitless/shared/contexts";

// Preserve established public identifiers while schema metadata is loading or
// unavailable. Unknown provider values wait behind a mask until classified.
const PUBLIC_PROVIDER_FIELDS = new Set([
  "provider_external_id",
  "client_id",
  "public_key",
  "language_code",
  "auth_type",
  "country_code",
  "phone_number",
  "registration_status",
  "pin_type",
  "pin_downloaded_at",
]);

const AgentDetailModal = ({
  agent,
  isOpen,
  onClose,
  autoConnectOAuth = false,
}) => {
  const { t } = useTranslation("aiAgents");
  const { locale } = useUiLocale();
  // Start in edit mode if autoConnectOAuth is true
  const [isEditing, setIsEditing] = useState(autoConnectOAuth);
  const [editedSettings, setEditedSettings] = useState({});
  const [editedFranchiseId, setEditedFranchiseId] = useState(
    String(agent?.franchise_id || 1),
  );

  const [editedBranchSelectionMode, setEditedBranchSelectionMode] = useState(
    getAgentBranchSelectionMode(agent),
  );
  const [editedProviderType, setEditedProviderType] = useState(
    agent?.provider_type || "chat",
  );
  const [showModeConfirmation, setShowModeConfirmation] = useState(false);

  // Track original values to detect changes
  const [originalSettings, setOriginalSettings] = useState({});
  const [originalBranchSelectionMode, setOriginalBranchSelectionMode] =
    useState(getAgentBranchSelectionMode(agent));
  const [editedWebviewColors, setEditedWebviewColors] = useState(() =>
    getAgentWebviewColors(agent),
  );
  const [originalWebviewColors, setOriginalWebviewColors] = useState(() =>
    getAgentWebviewColors(agent),
  );
  const [originalProviderType, setOriginalProviderType] = useState(
    agent?.provider_type || "chat",
  );
  const [originalFranchiseId, setOriginalFranchiseId] = useState(
    String(agent?.franchise_id || 1),
  );

  // State for franchise change confirmation
  const [showFranchiseConfirmation, setShowFranchiseConfirmation] =
    useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  // Fetch detailed agent data (for future use)
  // const { data: detailedAgent, isLoading: agentLoading } = useAiAgent(agent.id);

  // Fetch conversation data (for future use)
  // const { data: conversation } = useAgentCurrentConversation(agent.provider_external_id);
  // const { data: messages } = useAgentConversationMessages(agent.provider_external_id);

  // Hooks
  const updateAgentMutation = useUpdateAiAgent();
  // Reuses the provider-schema query already mounted by the AI page; no new
  // endpoint, mutation or permission. Standalone detail uses the same cache key.
  const activeProviderType =
    agent?.provider === "whatsapp" ? editedProviderType : agent?.provider_type;
  const { data: providerSchema } = useProviderSchema(
    agent?.provider,
    activeProviderType,
  );
  const {
    franchises,
    isLoading: franchisesLoading,
    isFetchingNextPage: franchisesFetchingNextPage,
    hasNextPage: franchisesHasNextPage,
    fetchNextPage: fetchNextFranchisesPage,
  } = useFranchisesInfinite({ enabled: !!isOpen });

  const agentFranchiseFromList = agent
    ? franchises.find((f) => f.id === agent.franchise_id)
    : undefined;
  const { data: agentFranchiseFallback } = useFranchise(
    isOpen && agent?.franchise_id && !agentFranchiseFromList
      ? agent.franchise_id
      : null,
  );
  const agentFranchiseDisplayName =
    agentFranchiseFromList?.name ?? agentFranchiseFallback?.name ?? "-";

  const origIdNum = Number.parseInt(originalFranchiseId, 10);
  const newIdNum = Number.parseInt(editedFranchiseId, 10);
  const origFranchiseInList = franchises.find((f) => f.id === origIdNum);
  const newFranchiseInList = franchises.find((f) => f.id === newIdNum);
  const { data: confirmOrigFranchiseFallback } = useFranchise(
    showFranchiseConfirmation &&
      !origFranchiseInList &&
      Number.isFinite(origIdNum)
      ? origIdNum
      : null,
  );
  const { data: confirmNewFranchiseFallback } = useFranchise(
    showFranchiseConfirmation &&
      !newFranchiseInList &&
      Number.isFinite(newIdNum)
      ? newIdNum
      : null,
  );
  const currentFranchiseConfirmName =
    origFranchiseInList?.name ??
    confirmOrigFranchiseFallback?.name ??
    t("common.unknown");
  const newFranchiseConfirmName =
    newFranchiseInList?.name ??
    confirmNewFranchiseFallback?.name ??
    t("common.unknown");

  const toast = useToast();
  const { trackActivation } = usePostHog();
  const { showMessage, isEnabled: isIntercomEnabled } = useIntercomContext();

  // Check if this is a WhatsApp agent with business_login auth
  const isWhatsApp = agent?.provider === "whatsapp";
  const isBusinessLogin =
    editedSettings?.auth_type === "business_login" ||
    agent?.provider_settings?.auth_type === "business_login";
  const showLanguageInModal = isWhatsApp && isBusinessLogin;

  // Handle language code change
  const handleLanguageChange = (e) => {
    const normalizedValue = normalizeLanguageCode(e.target.value);
    setEditedSettings((prev) => ({
      ...prev,
      language_code: normalizedValue,
    }));
  };

  // Initialize edited settings when agent changes
  React.useEffect(() => {
    if (agent) {
      const settings = normalizeSettingsLanguage(
        agent.provider,
        agent.provider_settings,
      );
      const franchiseId = String(agent.franchise_id || 1);

      setEditedSettings({
        ...settings,
        ...(agent.provider === "whatsapp"
          ? { provider_external_id: agent.provider_external_id || "" }
          : {}),
      });
      setEditedFranchiseId(franchiseId);
      setEditedBranchSelectionMode(getAgentBranchSelectionMode(agent));
      setEditedWebviewColors(getAgentWebviewColors(agent));
      setEditedProviderType(agent.provider_type || "chat");

      // Store original values for change detection
      setOriginalSettings({
        ...settings,
        ...(agent.provider === "whatsapp"
          ? { provider_external_id: agent.provider_external_id || "" }
          : {}),
      });
      setOriginalFranchiseId(franchiseId);
      setOriginalBranchSelectionMode(getAgentBranchSelectionMode(agent));
      setOriginalWebviewColors(getAgentWebviewColors(agent));
      setOriginalProviderType(agent.provider_type || "chat");
    }
  }, [agent]);

  const getProviderName = (provider) => {
    switch (provider) {
      case "whatsapp":
        return t("providers.whatsapp");
      case "phone":
        return t("providers.phone");
      case "web":
        return t("providers.web");
      default:
        return t("providers.default");
    }
  };

  const getProviderDescription = (provider) => {
    switch (provider) {
      case "whatsapp":
        return t("providerDescriptions.whatsapp");
      case "phone":
        return t("providerDescriptions.phone");
      case "web":
        return t("providerDescriptions.web");
      default:
        return t("providerDescriptions.default");
    }
  };

  const truncateValue = (value) => {
    // General truncation for very long strings
    if (typeof value === "string" && value.length > 50) {
      return `${value.substring(0, 47)}...`;
    }

    return value;
  };

  const handleFranchiseSelect = (franchiseId) => {
    setEditedFranchiseId(franchiseId);
  };

  // Helper function to detect changes in provider settings
  const getChangedSettings = () => {
    const changedSettings = {};

    // Check each setting for changes
    Object.keys(editedSettings).forEach((key) => {
      if (editedSettings[key] !== originalSettings[key]) {
        changedSettings[key] = editedSettings[key];
      }
    });

    // Check for new settings that weren't in original
    Object.keys(editedSettings).forEach((key) => {
      if (!(key in originalSettings) && editedSettings[key] !== "") {
        changedSettings[key] = editedSettings[key];
      }
    });

    return changedSettings;
  };

  // Only the WhatsApp Bot (guided webview, no AI) paints these colors
  const isWebviewBot = isWhatsApp && editedProviderType === "chat_bot";

  const getWebviewColorsPatch = () =>
    isWebviewBot
      ? buildWebviewColorsPatch(editedWebviewColors, originalWebviewColors)
      : {};

  // Helper function to check if there are any changes
  const hasChanges = () => {
    const changedSettings = getChangedSettings();
    const franchiseIdChanged = editedFranchiseId !== originalFranchiseId;
    const branchSelectionModeChanged =
      editedBranchSelectionMode !== originalBranchSelectionMode;
    const providerTypeChanged =
      agent?.provider === "whatsapp" &&
      editedProviderType !== originalProviderType;
    const webviewColorsChanged =
      Object.keys(getWebviewColorsPatch()).length > 0;

    return (
      Object.keys(changedSettings).length > 0 ||
      franchiseIdChanged ||
      branchSelectionModeChanged ||
      providerTypeChanged ||
      webviewColorsChanged
    );
  };

  // Helper function to build the update data payload
  const buildUpdatePayload = () => {
    if (!hasChanges()) {
      return null;
    }

    const updateData = {};
    const changedSettings = getChangedSettings();

    // auth_type can only be sent when changing to "manual"
    if (changedSettings.auth_type && changedSettings.auth_type !== "manual") {
      delete changedSettings.auth_type;
    }

    // Track if WhatsApp external_id was added for analytics
    const shouldTrackWhatsAppConnection =
      agent.provider === "whatsapp" &&
      editedSettings.provider_external_id &&
      !originalSettings.provider_external_id;

    // Handle provider_external_id changes
    if (
      Object.prototype.hasOwnProperty.call(
        changedSettings,
        "provider_external_id",
      ) &&
      editedSettings.provider_external_id !==
        originalSettings.provider_external_id
    ) {
      updateData.provider_external_id = editedSettings.provider_external_id;
      delete changedSettings.provider_external_id;
    }

    if (Object.keys(changedSettings).length > 0) {
      if (
        Object.prototype.hasOwnProperty.call(changedSettings, "language_code")
      ) {
        changedSettings.language_code = normalizeLanguageCode(
          changedSettings.language_code,
        );
      }
      updateData.provider_settings = changedSettings;
    }

    // Include franchise_id if it changed
    if (editedFranchiseId !== originalFranchiseId) {
      updateData.franchise_id = parseInt(editedFranchiseId) || 1;
    }

    // `settings` is merged per key by the API, so only send the keys that changed
    const settingsPatch = {
      ...(editedBranchSelectionMode !== originalBranchSelectionMode
        ? buildBranchSelectionSettings(editedBranchSelectionMode)
        : {}),
      ...getWebviewColorsPatch(),
    };
    if (Object.keys(settingsPatch).length > 0) {
      updateData.settings = settingsPatch;
    }

    if (
      agent.provider === "whatsapp" &&
      editedProviderType !== originalProviderType
    ) {
      updateData.provider_type = editedProviderType;
    }

    return {
      updateData,
      shouldTrackWhatsAppConnection,
    };
  };

  // Save current changes without closing modal (used before OAuth connect)
  const saveBeforeConnect = async () => {
    const payload = buildUpdatePayload();
    if (!payload) return true; // No changes to save, proceed

    const { updateData, shouldTrackWhatsAppConnection } = payload;

    try {
      await updateAgentMutation.mutateAsync({
        agentId: agent.id,
        ...updateData,
      });

      if (shouldTrackWhatsAppConnection) {
        trackActivation.whatsappConnected({
          agent_id: agent.id,
          provider_external_id: editedSettings.provider_external_id,
          franchise_id: agent.franchise_id,
        });
      }

      handleApiSuccess(t("detail.toasts.updateSuccess"), toast, {
        title: t("common.success"),
      });

      // Update original values to reflect the saved state
      setOriginalSettings(editedSettings);
      setOriginalFranchiseId(editedFranchiseId);
      setOriginalBranchSelectionMode(editedBranchSelectionMode);
      setOriginalWebviewColors(editedWebviewColors);

      return true;
    } catch (error) {
      handleApiError(agentErrorForFeedback(error), toast, {
        title: t("detail.toasts.updateErrorTitle"),
        defaultMessage: t("detail.toasts.updateErrorMessage"),
      });
      return false;
    }
  };

  // Main save handler - checks if franchise changed and shows confirmation
  const handleSaveChanges = async () => {
    try {
      const payload = buildUpdatePayload();

      if (!payload) {
        setIsEditing(false);
        return;
      }

      const { updateData, shouldTrackWhatsAppConnection } = payload;
      const providerTypeChanged = Object.prototype.hasOwnProperty.call(
        updateData,
        "provider_type",
      );

      if (providerTypeChanged) {
        setPendingUpdateData({
          updateData,
          shouldTrackWhatsAppConnection,
        });
        setShowModeConfirmation(true);
        return;
      }

      // Check if franchise_id is being changed
      const franchiseIdChanged = editedFranchiseId !== originalFranchiseId;

      if (franchiseIdChanged) {
        // Store pending changes and show confirmation modal
        setPendingUpdateData({
          updateData,
          shouldTrackWhatsAppConnection,
        });
        setShowFranchiseConfirmation(true);
        return; // Wait for user confirmation
      }

      // No franchise change, proceed directly
      await executeSave(updateData, shouldTrackWhatsAppConnection);
    } catch (error) {
      handleApiError(agentErrorForFeedback(error), toast, {
        title: t("detail.toasts.updateErrorTitle"),
        defaultMessage: t("detail.toasts.updateErrorMessage"),
      });
    }
  };

  // Executes the actual API call
  const executeSave = async (updateData, shouldTrackWhatsAppConnection) => {
    try {
      await updateAgentMutation.mutateAsync({
        agentId: agent.id,
        ...updateData,
      });

      // Track WhatsApp connection AFTER successful mutation
      if (shouldTrackWhatsAppConnection) {
        trackActivation.whatsappConnected({
          agent_id: agent.id,
          provider_external_id: editedSettings.provider_external_id,
          franchise_id: agent.franchise_id,
        });
      }

      handleApiSuccess(t("detail.toasts.updateSuccess"), toast, {
        title: t("common.success"),
      });

      // Update original values to reflect the saved state
      setOriginalSettings(editedSettings);
      setOriginalFranchiseId(editedFranchiseId);
      setOriginalBranchSelectionMode(editedBranchSelectionMode);
      setOriginalWebviewColors(editedWebviewColors);
      setOriginalProviderType(editedProviderType);

      setIsEditing(false);
      onClose?.();
    } catch (error) {
      handleApiError(agentErrorForFeedback(error), toast, {
        title: t("detail.toasts.updateErrorTitle"),
        defaultMessage: t("detail.toasts.updateErrorMessage"),
      });
    }
  };

  const handleModeChangeConfirmed = async () => {
    if (!pendingUpdateData) return;
    setShowModeConfirmation(false);
    if (editedFranchiseId !== originalFranchiseId) {
      setShowFranchiseConfirmation(true);
      return;
    }
    const { updateData, shouldTrackWhatsAppConnection } = pendingUpdateData;
    await executeSave(updateData, shouldTrackWhatsAppConnection);
    setPendingUpdateData(null);
  };

  const handleModeChangeCancelled = () => {
    setShowModeConfirmation(false);
    setPendingUpdateData(null);
  };

  // Handler for franchise change confirmation
  const handleFranchiseChangeConfirmed = async () => {
    if (!pendingUpdateData) return;

    const { updateData, shouldTrackWhatsAppConnection } = pendingUpdateData;

    // Close confirmation modal
    setShowFranchiseConfirmation(false);

    // Execute the save
    await executeSave(updateData, shouldTrackWhatsAppConnection);

    // Clear pending data
    setPendingUpdateData(null);
  };

  // Handler for franchise change cancellation
  const handleFranchiseChangeCancelled = () => {
    setShowFranchiseConfirmation(false);
    setPendingUpdateData(null);
    // Stay in edit mode - don't close the form
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center justify-between">
          <div className="flex items-center justify-center gap-3">
            <h1 className="text-foreground font-semibold">
              {getProviderName(agent.provider)}
            </h1>
            <div className="flex items-center justify-center gap-1">
              {(() => {
                const isEnabled = !!agent.enabled;
                const isConfigured = !!agent.configured;
                const reason = agent.misconfigured_reason;

                if (!isEnabled) {
                  return (
                    <>
                      <span className="border-destructive/30 text-destructive rounded-full border bg-[var(--error-bg)] px-3 py-1 text-sm font-medium">
                        {t("status.inactive")}
                      </span>
                      <ContextHelp
                        className="h-3.5 w-3.5"
                        ariaLabel={t("help.status.ariaLabel")}
                        whatIs={t("help.status.whatIs")}
                        note={t("help.status.inactive")}
                      />
                    </>
                  );
                }
                if (isConfigured) {
                  return (
                    <>
                      <span className="border-success/30 text-success rounded-full border bg-[var(--success-bg)] px-3 py-1 text-sm font-medium">
                        {t("status.active")}
                      </span>
                      <ContextHelp
                        className="h-3.5 w-3.5"
                        ariaLabel={t("help.status.ariaLabel")}
                        whatIs={t("help.status.whatIs")}
                        note={t("help.status.activeConfigured")}
                      />
                    </>
                  );
                }
                // Enabled but not configured
                let reasonText = null;
                if (reason) {
                  const reasonKey = `whatsappOAuth.errors.${reason}`;
                  const translatedReason = t(reasonKey);
                  reasonText =
                    translatedReason !== reasonKey ? translatedReason : reason;
                }
                return (
                  <>
                    <span className="border-warning/30 text-warning rounded-full border bg-[var(--warning-bg)] px-3 py-1 text-sm font-medium">
                      {t("status.active")}
                    </span>
                    <ContextHelp
                      className="h-3.5 w-3.5"
                      ariaLabel={t("help.status.ariaLabel")}
                      whatIs={t("help.status.whatIs")}
                      note={reasonText || t("help.status.notConfigured")}
                      noteClass="text-destructive"
                    />
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      }
      subtitle={getProviderDescription(agent.provider)}
      maxWidth="max-w-4xl"
    >
      <div className="ordering-ai-agent-modal-content space-y-8">
        {/* Agent Metrics */}
        {agent.metrics && (
          <div>
            <h3 className="text-foreground mb-4 text-lg font-semibold">
              {t("detail.metrics.title")}
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="border-border bg-card rounded-lg border p-4">
                <div className="text-center">
                  <div className="ordering-ai-agent-data-value text-success mb-1 text-2xl font-bold">
                    {agent.metrics.success_rate || 0}%
                  </div>
                  <div className="text-muted-foreground flex items-center justify-center gap-1 text-sm">
                    {t("metrics.successRate")}
                    <ContextHelp
                      ariaLabel={t("help.metrics.successRate.ariaLabel")}
                      whatIs={t("help.metrics.successRate.whatIs")}
                      formula={t("help.metrics.successRate.formula")}
                      period={t("help.metrics.successRate.period")}
                    />
                  </div>
                </div>
              </div>
              <div className="border-border bg-card rounded-lg border p-4">
                <div className="text-center">
                  <div className="ordering-ai-agent-data-value text-primary mb-1 text-2xl font-bold">
                    {convertTime(
                      agent.metrics.average_response_time_ms || 0,
                      "ms",
                      "s",
                      { decimals: 1 },
                    )}
                  </div>
                  <div className="text-muted-foreground flex items-center justify-center gap-1 text-sm">
                    {t("metrics.avgResponseTime")}
                    <ContextHelp
                      ariaLabel={t("help.metrics.avgResponseTime.ariaLabel")}
                      whatIs={t("help.metrics.avgResponseTime.whatIs")}
                      period={t("help.metrics.avgResponseTime.period")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sites & Domains - Only for finitless/chat agents */}
        {agent.provider === "finitless" && agent.provider_type === "chat" && (
          <AgentSitesSection agent={agent} isAgentEnabled={!!agent.enabled} />
        )}

        {/* Configuration */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-foreground text-lg font-semibold">
              {t("detail.configuration.title")}
            </h3>
          </div>

          {isEditing ? (
            <div className="space-y-6">
              {/* Brand Selector */}
              <div>
                <label
                  htmlFor="edit-agent-franchise-select"
                  className="text-muted-foreground mb-2 block text-[length:var(--font-size-product-ui)] font-medium"
                >
                  {t("detail.configuration.franchise")}
                </label>
                <FranchiseSelect
                  id="edit-agent-franchise-select"
                  value={editedFranchiseId}
                  onChange={handleFranchiseSelect}
                  disabled={franchisesLoading}
                  placeholder={t("detail.configuration.selectFranchise")}
                  placeholderLoading={t(
                    "detail.configuration.loadingFranchises",
                  )}
                  loadingMoreLabel={t(
                    "createModal.placeholders.loadingMoreFranchises",
                  )}
                  franchises={franchises}
                  isLoading={franchisesLoading}
                  isFetchingNextPage={franchisesFetchingNextPage}
                  hasNextPage={franchisesHasNextPage}
                  fetchNextPage={fetchNextFranchisesPage}
                  disabledSuffix={t("common.disabledSuffix")}
                  required
                  aria-label={t("detail.configuration.franchise")}
                />
              </div>

              {/* Branch selection mode */}
              <BranchSelectionModeField
                value={editedBranchSelectionMode}
                onChange={setEditedBranchSelectionMode}
              />

              {/* Agent Default Language - shown in modal for WhatsApp business_login */}
              {showLanguageInModal && (
                <div>
                  <label
                    htmlFor="edit-agent-language"
                    className="text-muted-foreground mb-2 block text-[length:var(--font-size-product-ui)] font-medium"
                  >
                    {t("settingsForm.languageTitle")}
                    <span aria-hidden="true" className="text-destructive ms-1">
                      *
                    </span>
                  </label>
                  <NativeSelect
                    id="edit-agent-language"
                    value={normalizeLanguageCode(
                      editedSettings?.language_code || "",
                    )}
                    onChange={handleLanguageChange}
                    className="w-full"
                    required
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(`settingsForm.languageOptions.${option.value}`)}
                      </option>
                    ))}
                  </NativeSelect>
                  {isIntercomEnabled && (
                    <Button
                      type="button"
                      emphasis="link"
                      intent="brand"
                      size="sm"
                      onClick={() =>
                        showMessage(
                          t("settingsForm.newLanguageIntercomPrefill"),
                        )
                      }
                      className="mt-1 h-auto px-0"
                    >
                      {t("settingsForm.newLanguageCta")}
                    </Button>
                  )}
                </div>
              )}

              {agent.provider === "whatsapp" && (
                <div>
                  <label
                    htmlFor="agent-mode"
                    className="text-muted-foreground mb-2 block text-[length:var(--font-size-product-ui)] font-medium"
                  >
                    {t("detail.mode.label")}
                  </label>
                  <NativeSelect
                    id="agent-mode"
                    data-testid="agent-mode-select"
                    value={
                      editedProviderType === "chat_bot" ? "chat_bot" : "chat"
                    }
                    onChange={(event) =>
                      setEditedProviderType(event.target.value)
                    }
                    className="w-full"
                  >
                    <option value="chat">{t("card.mode.ai")}</option>
                    <option value="chat_bot">{t("card.mode.bot")}</option>
                  </NativeSelect>
                </div>
              )}

              {/* WhatsApp Bot only: brand colors of the guided webview */}
              {isWebviewBot && (
                <AgentWebviewColorsField
                  value={editedWebviewColors}
                  onChange={setEditedWebviewColors}
                />
              )}

              {/* Provider Settings Form */}
              <ProviderSettingsForm
                provider={agent.provider}
                type={activeProviderType || "chat"}
                initialValues={editedSettings}
                onChange={setEditedSettings}
                readOnly={false}
                agent={agent}
                autoConnectOAuth={autoConnectOAuth}
                onSaveBeforeConnect={saveBeforeConnect}
              />
            </div>
          ) : (
            <div className="space-y-4">
              {(() => {
                // Fields to hide in view mode
                const hiddenFields = [
                  "access_token",
                  "meta_user_id",
                  "connected_at",
                  "waba_id",
                  "business_id",
                  "oauth_state",
                ];

                // Get configured display info
                const getConfiguredInfo = () => {
                  if (agent.configured) {
                    return { value: t("common:states.yes"), reason: null };
                  }
                  const reason = agent.misconfigured_reason;
                  if (reason) {
                    const displayReason = t(agentConfigurationErrorKey(reason));
                    return {
                      value: t("common:states.no"),
                      reason: displayReason,
                    };
                  }
                  return { value: t("common:states.no"), reason: null };
                };

                // Translate auth_type value for display
                const getAuthTypeLabel = (authType) => {
                  if (authType === "business_login") {
                    return t("whatsappOAuth.authTypeBusinessLogin");
                  }
                  if (authType === "manual") {
                    return t("whatsappOAuth.authTypeManual");
                  }
                  return authType;
                };

                const configuredInfo = getConfiguredInfo();

                // Transform provider_settings for display
                const transformedSettings = { ...agent.provider_settings };
                if (transformedSettings.auth_type) {
                  transformedSettings.auth_type = getAuthTypeLabel(
                    transformedSettings.auth_type,
                  );
                }
                if (!isWhatsApp) {
                  delete transformedSettings.pin_downloaded_at;
                } else if (!transformedSettings.pin_downloaded_at) {
                  transformedSettings.pin_downloaded_at = t(
                    "whatsappOAuth.info.notDownloaded",
                  );
                }
                if (transformedSettings.registration_status) {
                  const status =
                    transformedSettings.registration_status.toUpperCase();
                  if (status === "CONNECTED") {
                    transformedSettings.registration_status = t(
                      "whatsappOAuth.registrationStatus.connected",
                    );
                  } else if (status === "FLAGGED") {
                    transformedSettings.registration_status = t(
                      "whatsappOAuth.registrationStatus.flagged",
                    );
                  } else if (status === "RESTRICTED") {
                    transformedSettings.registration_status = t(
                      "whatsappOAuth.registrationStatus.restricted",
                    );
                  } else if (status === "OFFLINE") {
                    transformedSettings.registration_status = t(
                      "whatsappOAuth.registrationStatus.offline",
                    );
                  }
                }
                if (transformedSettings.pin_type) {
                  const pinType = transformedSettings.pin_type.toLowerCase();
                  if (pinType === "generated") {
                    transformedSettings.pin_type = t(
                      "whatsappOAuth.pinType.generated",
                    );
                  } else if (pinType === "manual") {
                    transformedSettings.pin_type = t(
                      "whatsappOAuth.pinType.manual",
                    );
                  }
                }
                if (transformedSettings.language_code) {
                  try {
                    const displayName = new Intl.DisplayNames(
                      [locale || "en"],
                      { type: "language" },
                    );
                    const name = displayName.of(
                      transformedSettings.language_code,
                    );
                    if (name) {
                      transformedSettings.language_code = name;
                    }
                  } catch {
                    // Keep the code as-is if Intl fails
                  }
                }

                const displaySettings = {
                  ...transformedSettings,
                  ...(agent.franchise_id && {
                    franchise_id: agentFranchiseDisplayName,
                  }),
                  branch_selection_mode: t(
                    `branchSelection.options.${getAgentBranchSelectionMode(
                      agent,
                    )}.label`,
                  ),
                  configured: configuredInfo.value,
                };

                // Filter out hidden fields
                const filteredSettings = Object.fromEntries(
                  Object.entries(displaySettings).filter(
                    ([key]) => !hiddenFields.includes(key),
                  ),
                );

                return Object.keys(filteredSettings).length > 0 ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {Object.entries(filteredSettings).map(([key, rawValue]) => {
                      const settingSchema =
                        providerSchema?.properties?.[key] ??
                        (!providerSchema &&
                        Object.hasOwn(agent.provider_settings || {}, key) &&
                        !PUBLIC_PROVIDER_FIELDS.has(key)
                          ? { writeOnly: true }
                          : undefined);
                      const safeValue = maskAgentSetting(
                        key,
                        rawValue,
                        settingSchema,
                      );
                      const value =
                        typeof safeValue === "string"
                          ? safeValue
                          : JSON.stringify(safeValue);
                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between"
                        >
                          <span className="text-muted-foreground text-sm capitalize">
                            {resolveAgentSettingLabel({
                              key,
                              t,
                              locale,
                            })}
                          </span>
                          {key === "configured" && configuredInfo.reason ? (
                            <span className="text-foreground flex items-center gap-1 text-sm font-medium">
                              {value}
                              <ContextHelp
                                ariaLabel={t(
                                  "detail.configuration.configuredHelp",
                                )}
                                whatIs={configuredInfo.reason}
                              />
                            </span>
                          ) : (
                            <span
                              className="text-foreground max-w-xs truncate text-sm font-medium"
                              title={
                                typeof value === "string"
                                  ? value
                                  : JSON.stringify(value)
                              }
                            >
                              {typeof value === "string"
                                ? truncateValue(value)
                                : truncateValue(JSON.stringify(value))}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    {t("detail.configuration.empty")}
                  </p>
                );
              })()}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="border-border flex gap-3 border-t pt-6 pb-24 sm:pb-0">
          {isEditing ? (
            <>
              <Button
                type="button"
                emphasis="outline"
                intent="neutral"
                size="touch"
                onClick={() => setIsEditing(false)}
                className="flex-1"
              >
                {t("actions.cancel")}
              </Button>
              <Button
                type="button"
                emphasis="solid"
                intent="brand"
                size="touch"
                onClick={handleSaveChanges}
                disabled={updateAgentMutation.isPending || !hasChanges()}
                className="flex-1"
              >
                {updateAgentMutation.isPending
                  ? t("actions.saving")
                  : t("actions.saveChanges")}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                emphasis="solid"
                intent="brand"
                size="touch"
                onClick={() => setIsEditing(true)}
                className="flex-1"
              >
                {t("actions.editAgent")}
              </Button>
              {isWhatsApp && (
                <Button
                  type="button"
                  emphasis="outline"
                  intent="neutral"
                  size="touch"
                  onClick={() => setShowTemplatesModal(true)}
                  disabled={!agent?.configured}
                  title={
                    !agent?.configured
                      ? t("templates.disabledNotConfigured")
                      : undefined
                  }
                  className="flex-1"
                >
                  {t("templates.viewButton")}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* WhatsApp Templates Modal */}
      {isWhatsApp && (
        <AgentTemplatesModal
          isOpen={showTemplatesModal}
          onClose={() => setShowTemplatesModal(false)}
          agent={agent}
        />
      )}

      {/* Franchise Change Confirmation Modal */}
      {showModeConfirmation && (
        <div
          role="dialog"
          aria-labelledby="agent-mode-confirm-title"
          data-testid="mode-confirmation"
          className="border-border bg-card space-y-4 rounded-lg border p-4"
        >
          <h4
            id="agent-mode-confirm-title"
            className="text-foreground font-semibold"
          >
            {t("detail.mode.confirmTitle")}
          </h4>
          <p className="text-muted-foreground text-sm">
            {t("detail.mode.confirmBody")}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              emphasis="outline"
              intent="neutral"
              size="touch"
              onClick={handleModeChangeCancelled}
            >
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              emphasis="solid"
              intent="brand"
              size="touch"
              data-testid="confirm-mode-change"
              onClick={handleModeChangeConfirmed}
            >
              {t("detail.mode.confirm")}
            </Button>
          </div>
        </div>
      )}

      {showFranchiseConfirmation && (
        <FranchiseChangeConfirmationModal
          isOpen={showFranchiseConfirmation}
          onClose={handleFranchiseChangeCancelled}
          onConfirm={handleFranchiseChangeConfirmed}
          currentFranchiseName={currentFranchiseConfirmName}
          newFranchiseName={newFranchiseConfirmName}
          isLoading={updateAgentMutation.isPending}
        />
      )}
    </Modal>
  );
};

export default AgentDetailModal;
